import { toaster } from '@/components/ui/toaster';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface EmailConnectCallbackOptions {
  /** URL param the backend sets on its way back, e.g. 'gmail' or 'outlook'. */
  callbackParam: string;
  /** i18n prefix holding `success.connected` and `errors.connectionFailed`. */
  i18nPrefix: string;
  /** Extra refresh a surface needs once the mailbox is connected. */
  onConnected?: () => void;
}

/**
 * Reads the mailbox OAuth result the backend appends to the URL (`?gmail=connected`,
 * `?outlook=error&reason=...`), tells the brand, refreshes the integrations and strips the
 * param so a reload does not replay it. Runs once per mount.
 */
export const useEmailConnectCallback = ({
  callbackParam,
  i18nPrefix,
  onConnected,
}: EmailConnectCallbackOptions) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const callbackProcessed = useRef(false);

  useEffect(() => {
    if (callbackProcessed.current) return;

    const result = new URLSearchParams(window.location.search).get(callbackParam);
    if (!result) return;

    callbackProcessed.current = true;
    // Clean URL immediately
    window.history.replaceState({}, '', window.location.pathname);

    if (result === 'connected') {
      toaster.create({ description: t(`${i18nPrefix}.success.connected`), type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onConnected?.();
    } else if (result === 'error') {
      toaster.create({ description: t(`${i18nPrefix}.errors.connectionFailed`), type: 'error' });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally runs once
};
